import { AnyAction } from 'redux';
import { STATE } from 'app/constants';
import { ApplicationState } from 'app/types-store';
import { ActionDispatching } from 'app/types-shared';
import { complexValidationFromServer } from 'app/infrastructure/data/validation';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { ErrorNotification, SuccessNotification } from 'app/infrastructure/notifications/user-messages';
import { isForbiddenStatus, blockAccess, isNotFoundStatus } from 'app/area/shared/actions-utils';
import * as navigate from 'app/area/shared/navigation';

import { DefaultFilter } from 'app/area/shared/default-filters/types-store';
import { mapFiltersFromWire } from 'app/area/shared/default-filters/utils/map-filters';
import { mapFilterDomainsToFilter } from 'app/area/shared/default-filters/utils/map-filter-domains';
import { mapFilterMetadata } from 'app/area/shared/default-filters/utils/map-filter-metadata';
import { actionCreators as defaultFiltersActionCreators } from 'app/area/shared/default-filters/actions';
import { api as defaultFiltersApi } from 'app/area/shared/default-filters/api';

import { mapSavedReportsFromWire } from 'app/area/shared/saved-reports/utils/map-saved-reports';
import { mapSavedReportsValidation } from 'app/area/shared/saved-reports/utils/map-saved-reports-validation';
import { actionCreators as commonActionCreators } from 'app/area/shared/saved-reports/actions';
import { api as commonApi } from 'app/area/shared/saved-reports/api';
import { mapSavedReportsWarnings } from 'app/area/shared/saved-reports/utils/map-saved-reports-warnings';
import { SavedReport } from 'app/area/shared/saved-reports/types-store';

import { getBackRoute } from '../common/route';
import { mapDashboardFromWire, mapDashboardToWire } from './utils/map-dashboard';
import { endpoints as commonEndpoints } from '../common/endpoints';
import { dashboardAdminTranslation } from '../constants';
import {
  RequestLanguageOptions, ReceiveLanguageOptions, FailLanguageOptions,
  RequestDashboard, ReceiveDashboard, FailDashboard,
  RequestDeleteDashboard, ReceiveDeleteDashboard, FailDeleteDashboard,
  RequestSaveDashboard, ReceiveSaveDashboard, FailSaveDashboard,
  UpdateItem, ChangeLanguage, CopyLanguageToAll,
  ClearForm, UpdateValidation
} from './action-types';
import { api } from './api';
import { DashboardComponentForm, limitedByUsersAccess } from './constants';
import { endpoints } from './endpoints';
import { DashboardItem, DashboardItemValidation } from './types-store';

export const actionCreators = {

  ...commonActionCreators(DashboardComponentForm, commonEndpoints),
  ...defaultFiltersActionCreators(DashboardComponentForm, endpoints),

  loadLanguageOptions: (): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      dispatch(new RequestLanguageOptions());

      try {

        const languageOptions = await api.loadLanguageOptions();

        dispatch(new ReceiveLanguageOptions(
          languageOptions.map(option => ({
            value: option.v,
            description: option.d
          }))
        ));
      } catch (err) {

        if (err && (err.userMessages || err.message)) {
          dispatch(new ErrorNotification(err.userMessages || err.message));
        }
        dispatch(new FailLanguageOptions());
      }
    },

  createDashboard: (): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      dispatch(new RequestDashboard(undefined));

      try {

        const [rootUserGroups, rootCaseTypes, accessTypes, filterDomains] = await Promise.all([
          api.loadRootUserGroups(),
          api.loadRootCaseTypes(),
          api.loadAccessTypes(),
          defaultFiltersApi.loadFilterDomains(endpoints)
        ]);

        const filtersMetadata = filterDomains?.filterDomain?.length > 0
          ? await defaultFiltersApi.loadFiltersMetadata(endpoints, filterDomains.filterDomain.map(filter => filter.domain))
          : [];

        dispatch(new ReceiveDashboard(undefined, mapDashboardFromWire({
          subset: rootUserGroups?.rootCodes,
          application: rootCaseTypes?.rootCodes,
          accessType: accessTypes?.find(type => type.v === limitedByUsersAccess)
        }), [], mapFilterDomainsToFilter(filterDomains), filtersMetadata?.map(mapFilterMetadata) ?? []));

      } catch (err) {

        if (isForbiddenStatus(err)) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.dashboardAdministration,
            translationId: dashboardAdminTranslation.no_access_to_dashboard_admin
          });
        } else {

          const errorMessage = err && (err.userMessages || err.message);
          !!errorMessage && dispatch(new ErrorNotification(errorMessage));

          navigate.to({ pathname: getBackRoute() });
        }

        dispatch(new FailDashboard(undefined));
      }
    },

  loadDashboard: (id: number, dashboardFavouriteId: number): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      dispatch(new RequestDashboard(id));

      try {

        const dashboard = await api.loadDashboard(id);

        const evaluationsWireRequest = commonApi.loadSavedReportEvaluations(commonEndpoints,
          dashboard?.savedReport?.map(report => report.code?.v));

        const filtersMetadataRequest = dashboard?.filter?.length > 0
          ? defaultFiltersApi.loadFiltersMetadata(endpoints, dashboard?.filter?.map(filter => filter.domain?.v))
          : [];

        const [evaluationsWire, filtersMetadata] = await Promise.all([evaluationsWireRequest, filtersMetadataRequest]);

        dispatch(new ReceiveDashboard(id,
          mapDashboardFromWire(dashboard),
          mapSavedReportsFromWire(dashboard?.savedReport, evaluationsWire),
          mapFiltersFromWire(dashboard?.filter, filtersMetadata),
          filtersMetadata.map(mapFilterMetadata)
        ));

        const evaluationErrors = mapSavedReportsWarnings(evaluationsWire);

        if (Object.keys(evaluationErrors).length > 0) {
          actionCreators.updateSavedReportValidation(evaluationErrors)(dispatch, getState);
        }

      } catch (err) {

        if (isForbiddenStatus(err)) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.dashboardAdministration,
            translationId: dashboardAdminTranslation.no_access_to_dashboard_admin
          });
        } else {

          const errorMessage = err && (err.userMessages || err.message);
          !!errorMessage && dispatch(new ErrorNotification(errorMessage));

          navigate.to({ pathname: getBackRoute(id, dashboardFavouriteId) });
        }

        dispatch(new FailDashboard(id));
      }
    },

  deleteDashboard: (id: number, dashboardFavouriteId: number, successMessage: string): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      dispatch(new RequestDeleteDashboard(id));

      try {
        await api.deleteDashboard(id);

        navigate.to({ pathname: getBackRoute(), state: { internal: true } });

        dispatch(new ReceiveDeleteDashboard(id));
        dispatch(new SuccessNotification(successMessage));
      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
        dispatch(new FailDeleteDashboard(id));

        if (isNotFoundStatus(err)) {
          navigate.to({ pathname: getBackRoute(), state: { internal: true } });
        }
      }
    },

  saveDashboard: (
    dashboard: DashboardItem,
    savedReports: SavedReport[],
    filters: DefaultFilter[],
    dashboardFavouriteId: number,
    successMessage: string,
    validationErrorMessage: string
  ): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      const id = dashboard?.id;

      dispatch(new RequestSaveDashboard(id));

      try {

        const dashboardWire = mapDashboardToWire(dashboard, savedReports, filters);

        if (isDefinedIncludingZero(id)) {
          await api.saveDashboard(id, dashboardWire);

          dispatch(new ReceiveSaveDashboard(id, undefined));

          navigate.to({ pathname: getBackRoute(id, dashboardFavouriteId), state: { internal: true } });
        } else {
          const newDashboardId = await api.createDashboard(dashboardWire);

          if (savedReports?.length > 0) {

            dispatch(new ReceiveSaveDashboard(id, undefined));

            navigate.to({ pathname: getBackRoute(id, dashboardFavouriteId), state: { internal: true } });
          } else {
            dispatch(new ReceiveSaveDashboard(id, newDashboardId));
          }
        }

        dispatch(new SuccessNotification(successMessage));
      } catch (err) {

        if (err?.userMessages?.length ?? 0 > 0) {
          dispatch(new ErrorNotification(err.userMessages));
        }

        if (Object.keys(err?.validationErrors ?? {}).length > 0) {
          dispatch(new ErrorNotification(validationErrorMessage));

          const { savedReport: code, filter, ...validation } = err.validationErrors;

          dispatch(new UpdateValidation(complexValidationFromServer(validation)));
          actionCreators.updateSavedReportValidation(mapSavedReportsValidation({ code }))(dispatch, getState);
          actionCreators.updateFilterValidation(filter)(dispatch, getState);
        }

        dispatch(new FailSaveDashboard(id));

        if (isNotFoundStatus(err)) {
          navigate.to({ pathname: getBackRoute(id, dashboardFavouriteId), state: { internal: true } });
        }
      }
    },

  clearForm: (): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ClearForm());
    },

  updateItem: (item: Partial<DashboardItem>): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new UpdateItem(item));
    },

  changeLanguage: (languageId: number, text: string): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ChangeLanguage(languageId, text));
    },

  copyLanguageToAll: (languageId: number, successMessage: string): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new CopyLanguageToAll(languageId));
      dispatch(new SuccessNotification(successMessage));
    },

  updateValidation: (validation: DashboardItemValidation): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new UpdateValidation(validation));
    },

  displayError: (errorMessage: string): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ErrorNotification(errorMessage));
    }
};
